/**
 * SyncPdvDesktopService
 *
 * Fachada de sincronização para o módulo PDV Desktop.
 * Delega para o SincronizacaoService central, expondo métodos
 * específicos para cada entidade.
 *
 * Só funciona em modo Electron/Desktop.
 */
import SincronizacaoService from "@/services/SincronizacaoService";
import { isDesktopMode, getElectronAPI } from "@/plugins/desktop";
import { SYSTEM_VARIABLE } from "@/modules/Application";
import ApiOfflineService from "./ApiOfflineService";
import CaixaSyncService from "./CaixaSyncService";
import TerminalConfigService from "./TerminalConfigService";
import AppConfigRepository from "@/modules/PdvDesktop/repositories/AppConfigRepository";
import FiscalContextPdvDesktopService from "./FiscalContextPdvDesktopService";
import { normalizarPagamentoFiscal } from "./NfcePagamentoFiscalMapper";
import NfceStatusSyncService from "./NfceStatusSyncService";

async function _getCredentials() {
  const api    = getElectronAPI();
  const token  = await api.config.get("api_token");
  const apiUrl = (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");
  return { apiUrl, token };
}

const _round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function _safeParse(json) {
  if (!json) return {};
  if (typeof json === "object") return json;
  try { return JSON.parse(json); } catch (_) { return {}; }
}

/**
 * Monta o payload completo de venda (cliente, itens, pagamentos, descontos,
 * acréscimos, troco, dados de lote/combinação/produto pesado) a ser enviado
 * junto com o XML assinado para o endpoint /offline/nfce/transmitir-xml.
 *
 * O backend usa este payload para gerar financeiro e movimentar estoque
 * após autorização da NFC-e pela SEFAZ.
 *
 * Regras:
 *  - valor_total = nfce.valorTotal (que casa com o vNF do XML)
 *  - Σ pagamentos deve bater com valor_total (exceto quando há troco)
 *  - itens contêm valor_bruto, desconto, acrescimo, valor_liquido
 *  - lote: id_produto_lote, numero_lote e quantidade real do item
 *  - combinação: id_produto_combinacao + descricao_combinacao
 *  - pesado: quantidade decimal + peso_liquido + codigo_balanca
 *
 * @param {object} nfce - linha de nfce_local com itens[] e pagamentos[] carregados
 */
function _buildVendaPayloadParaTransmissao(nfce) {
  const dadosWrapper   = _safeParse(nfce.payload_json);
  const dadosOriginais = dadosWrapper.dados || {};
  const dadosJson      = _safeParse(nfce.dados_json);

  const buyer = dadosOriginais.buyer ?? dadosJson.buyer ?? null;
  const cliente = buyer ? {
    id_cliente:    buyer.id_cliente    ?? buyer.id_pessoa ?? buyer.id ?? null,
    uuid:          buyer.uuid          ?? null,
    cnpj_cpf:      buyer.cnpj_cpf      ?? buyer.cpf   ?? null,
    nome:          buyer.nome          ?? buyer.razao_social ?? null,
    nome_fantasia: buyer.nome_fantasia ?? null,
    email:         buyer.email         ?? null,
    telefone:      buyer.telefone      ?? buyer.celular ?? null,
    ie:            buyer.ie            ?? null,
  } : null;

  const itens = (nfce.itens || []).map((it, idx) => {
    const itemPayload     = _safeParse(it.payload_json);
    // combinacao_json = { "Tamanhos": "P", "quantidade": 1 } — salvo por _normalizarItem
    const combinacaoData  = itemPayload.combinacao_json ?? itemPayload.combinacao ?? null;
    // loteOriginal = objeto do lote com o saldo (quantidade do estoque)
    const loteOriginal    = itemPayload.lote_original   ?? null;
    // loteBaixa    = objeto do lote com a quantidade VENDIDA neste item
    const loteBaixa       = itemPayload.lote_baixa_json ?? null;
    const produtoOriginal = itemPayload.produto_original ?? null;

    const qtd     = Number(it.quantidade     || 0);
    const unit    = Number(it.valor_unitario || 0);
    const bruto   = it.total_bruto   != null ? Number(it.total_bruto)   : _round2(qtd * unit);
    const liquido = it.total_liquido != null ? Number(it.total_liquido) : Number(it.total ?? 0);

    // Monta o objeto product no formato web-compatible esperado pelo backend
    const prodObj = produtoOriginal ? {
      id:                 produtoOriginal.id_produto ?? produtoOriginal.id ?? it.id_produto ?? null,
      id_empresa:         produtoOriginal.id_empresa ?? null,
      codigo:             produtoOriginal.codigo != null ? String(produtoOriginal.codigo) : (it.codigo ?? null),
      descricao:          produtoOriginal.descricao ?? it.descricao ?? null,
      preco:              unit,
      custo:              produtoOriginal.custo ?? null,
      quantidade:         qtd,
      variavel:           !!(produtoOriginal.variavel),
      combinacao:         combinacaoData,
      unidade_medida:     produtoOriginal.unidade_medida ?? null,
      id_ncm:             produtoOriginal.id_ncm         ?? null,
      id_cfop:            produtoOriginal.id_cfop        ?? null,
      id_cst_csosn:       produtoOriginal.id_cst_csosn   ?? null,
      id_origem:          produtoOriginal.id_origem       ?? null,
      preco_atacado:      produtoOriginal.preco_atacado   ?? null,
      quantidade_atacado: produtoOriginal.quantidade_atacado ?? null,
      pesado:             !!it.pesado,
      // lote (boolean): indica que o produto controla lote
      lote:               !!(loteOriginal || it.id_produto_lote),
      baixa_automatica_lote: !!(produtoOriginal.baixa_automatica_lote),
      // lotes: array com saldo disponível (quantidade original do estoque)
      lotes:              loteOriginal ? [loteOriginal] : (produtoOriginal.lotes ?? null),
      // lote_baixa: lote selecionado com a quantidade VENDIDA
      lote_baixa:         loteBaixa,
      total:              _round2(liquido),
      tipo_atacado:       (itemPayload.tipo_preco_aplicado ?? null) === "atacado",
    } : null;

    return {
      nItem:                 it.nItem ?? (idx + 1),
      id_produto:            it.id_produto    ?? null,
      codigo:                it.codigo        ?? null,
      descricao:             it.descricao     ?? null,
      cod_barras:            it.cod_barras    ?? null,
      unidade_medida:        it.unidade_medida_sigla ?? null,
      ncm:                   it.ncm_codigo    ?? null,
      cfop:                  it.cfop_codigo   ?? null,
      quantidade:            qtd,
      valor_unitario:        unit,
      valor_bruto:           _round2(bruto),
      desconto:              _round2(it.desconto  ?? 0),
      acrescimo:             _round2(it.acrescimo ?? 0),
      desconto_percentual:   Number(it.desconto_percentual  ?? 0),
      acrescimo_percentual:  Number(it.acrescimo_percentual ?? 0),
      valor_liquido:         _round2(liquido),
      ajuste_tipo:           it.ajuste_tipo  ?? null,
      ajuste_forma:          it.ajuste_forma ?? null,
      // ── Lote ────────────────────────────────────────────────────────────
      id_produto_lote:       it.id_produto_lote ?? (loteOriginal ? loteOriginal.id_produto_lote ?? null : null),
      numero_lote:           it.numero_lote     ?? (loteOriginal ? loteOriginal.numero_lote     ?? null : null),
      data_validade:         it.data_validade   ?? (loteOriginal ? loteOriginal.data_validade   ?? null : null),
      data_fabricacao:       it.data_fabricacao ?? (loteOriginal ? loteOriginal.data_fabricacao ?? null : null),
      // ── Combinação ──────────────────────────────────────────────────────
      id_produto_combinacao: itemPayload.id_produto_combinacao ?? (combinacaoData ? combinacaoData.id_produto_combinacao ?? null : null),
      descricao_combinacao:  itemPayload.descricao_combinacao  ?? (combinacaoData ? combinacaoData.descricao_combinacao  ?? null : null),
      // ── Produto pesado / balança ────────────────────────────────────────
      pesado:                !!it.pesado,
      peso_liquido:          it.peso_liquido   != null ? Number(it.peso_liquido) : null,
      codigo_balanca:        it.codigo_balanca ?? null,
      // ── Formato web-compatible para o backend ────────────────────────────
      product:    prodObj,
      price:      unit,
      totalPrice: _round2(liquido),
      quantity:   qtd,
      currentIndex: it.nItem ?? (idx + 1),
      active:     true,
      combinacao: combinacaoData,
      // item.lote = lote selecionado com quantidade vendida (mesmo que product.lote_baixa)
      lote:       loteBaixa,
    };
  });

  console.log("[PDV_PAYLOAD_TRANSMISSAO_ITENS]", itens);
  itens.forEach((item, idx) => {
    if (item.id_produto_lote || item.product?.lote) {
      console.log(`[PDV_PAYLOAD_LOTE] item[${idx}]:`, {
        id_produto_lote:       item.id_produto_lote,
        product_lote_flag:     item.product?.lote,
        product_lote_baixa:    item.product?.lote_baixa,
        item_lote:             item.lote,
      });
    }
  });

  const valorTotal = _round2(nfce.valorTotal);

  let somaPagamentos = 0;
  const pagamentos = (nfce.pagamentos || []).map((p) => {
    const pPayload = _safeParse(p.payload_json);
    const valor    = Number(p.valor || 0);
    somaPagamentos += valor;
    const pagamentoBase = {
      id_forma_pagamento: p.id_forma_pagamento ?? null,
      id_meio_pagamento:  p.id_meio_pagamento  ?? null,
      descricao:          p.descricao ?? pPayload.descricao ?? null,
      valor:              _round2(valor),
      parcelas:           p.parcelas           ?? 1,
      vencimento:         p.vencimento         ?? null,
      tpag:               pPayload.tpag ?? pPayload.tPag ?? p.tpag ?? null,
      xPag:               pPayload.xPag ?? pPayload.xpag ?? null,
    };
    return normalizarPagamentoFiscal(pagamentoBase);
  });
  somaPagamentos = _round2(somaPagamentos);
  const troco = somaPagamentos > valorTotal ? _round2(somaPagamentos - valorTotal) : 0;

  const descontoTotal = Number(
    dadosOriginais.desconto ?? dadosOriginais.discount ?? dadosJson.discount
      ?? itens.reduce((s, i) => s + (i.desconto  || 0), 0),
  );
  const acrescimoTotal = Number(
    dadosOriginais.acrescimo ?? dadosOriginais.extraValue ?? dadosJson.extraValue
      ?? itens.reduce((s, i) => s + (i.acrescimo || 0), 0),
  );

  return {
    valor_total: valorTotal,
    desconto:    _round2(descontoTotal),
    acrescimo:   _round2(acrescimoTotal),
    troco,
    cliente,
    itens,
    pagamentos,
    id_vendedor: dadosOriginais.id_vendedor ?? dadosJson.id_vendedor ?? nfce.id_vendedor ?? null,
  };
}

const MENSAGEM_CONFIG_NFCE_TERMINAL_AUSENTE =
  "Configuração NFC-e do terminal não encontrada. Sincronize os dados fiscais antes de transmitir.";

const _isBlank = (value) => value == null || String(value).trim() === "";

async function _obterTerminalConfigFiscalParaTransmissao(api) {
  const [terminalInfo, configuracaoNfce, device_id] = await Promise.all([
    api.terminal.getInfo(),
    api.configuracaoNfce.buscarConfiguracao(),
    AppConfigRepository.getDeviceId(),
  ]);

  const uuid_terminal = terminalInfo?.uuid_terminal ?? null;
  const id_terminal_venda = terminalInfo?.id_terminal_venda != null
    ? Number(terminalInfo.id_terminal_venda)
    : null;
  const id_configuracao = terminalInfo?.id_configuracao != null
    ? Number(terminalInfo.id_configuracao)
    : null;
  const ambiente = configuracaoNfce?.ambiente ?? null;
  const modelo = configuracaoNfce?.modelo ?? 65;
  const serie = configuracaoNfce?.serie ?? null;

  if (_isBlank(id_terminal_venda) || _isBlank(id_configuracao)) {
    throw new Error(MENSAGEM_CONFIG_NFCE_TERMINAL_AUSENTE);
  }

  if (
    _isBlank(uuid_terminal) ||
    _isBlank(device_id) ||
    _isBlank(ambiente) ||
    _isBlank(serie) ||
    Number(modelo) !== 65
  ) {
    throw new Error(
      "Configuração fiscal do terminal incompleta. Sincronize os dados fiscais antes de transmitir."
    );
  }

  return {
    uuid_terminal,
    device_id: String(device_id),
    id_terminal_venda: Number(id_terminal_venda),
    id_configuracao: Number(id_configuracao),
    id_configuracao_nfce: Number(id_configuracao),
    ambiente: Number(ambiente),
    modelo: Number(modelo),
    serie: Number(serie),
  };
}

function _montarEntradaTransmissaoComContexto(nfce, terminalConfig) {
  return {
    uuid_venda_terminal: nfce.uuid_venda_terminal,
    chave:               nfce.chave,
    xml_assinado:        nfce.xml_assinado,
    id_terminal_venda:   terminalConfig.id_terminal_venda,
    id_configuracao:     terminalConfig.id_configuracao,
    id_configuracao_nfce: terminalConfig.id_configuracao_nfce,
    serie:               terminalConfig.serie ?? nfce.serie,
    numero:              nfce.numero ?? nfce.numeroNota,
    modelo:              terminalConfig.modelo ?? nfce.modelo ?? 65,
    emitida_em:          nfce.dtVenda ?? nfce.emissao ?? nfce.created_at,
    venda:               _buildVendaPayloadParaTransmissao(nfce),
  };
}

const SyncPdvDesktopService = {
  /**
   * Baixa um endpoint paginado percorrendo TODAS as páginas até terminar.
   *
   * Robusto a diferentes formatos de back-end:
   *  - paginator Laravel ({ data, last_page, current_page, total, ... });
   *  - array puro que respeita ?page (continua enquanto a página vier cheia);
   *  - array puro que ignora ?page (interrompe ao detectar que não chegam
   *    novos ids, evitando loop infinito / duplicatas).
   *
   * A persistência de cada página é feita pelo callback onPagina(items), que
   * deve devolver quantos registros foram efetivamente salvos.
   *
   * IMPORTANTE: o chamador só deve atualizar a "última sincronização" depois
   * que este método retornar (ou seja, após concluir todas as páginas).
   *
   * @returns {Promise<{ totalRecebido: number, totalSalvo: number, paginas: number }>}
   */
  async _baixarPaginado({ baseUrl, headers, path, perPage = 200, query = "", idOf, onPagina, onProgress, label = "Registros" }) {
    const extrairId = idOf || ((it) =>
      it?.id ?? it?.id_remoto ?? it?.id_produto ?? it?.id_cliente ??
      it?.id_produto_combinacao ?? it?.id_produto_lote ?? it?.id_vendedor ??
      it?.id_grupo ?? it?.id_lista_preco ?? it?.uuid ?? null);

    const vistos = new Set();
    const MAX_PAGINAS = 100000; // trava de segurança contra loop infinito

    let pagina        = 1;
    let lastPage      = null;
    let totalBack     = null;
    let totalRecebido = 0;
    let totalSalvo    = 0;
    let paginas       = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const sep = path.includes("?") ? "&" : "?";
      const url = `${baseUrl}${path}${sep}page=${pagina}&per_page=${perPage}${query}`;
      const resp = await fetch(url, { headers });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const json  = await resp.json();
      const items = Array.isArray(json) ? json : (json.data ?? json.items ?? []);

      // Captura metadados de paginação do back (apenas se vier objeto, não array puro)
      const meta = (json && !Array.isArray(json)) ? json : {};
      const metaTotal       = meta.total        ?? meta.meta?.total        ?? null;
      const metaPerPage     = meta.per_page     ?? meta.meta?.per_page     ?? null;
      const metaCurrentPage = meta.current_page ?? meta.meta?.current_page ?? null;
      const metaLastPage    = meta.last_page    ?? meta.meta?.last_page    ?? null;

      if (lastPage === null && metaLastPage != null) lastPage = Number(metaLastPage);
      if (totalBack === null && metaTotal != null)   totalBack = Number(metaTotal);

      paginas++;
      totalRecebido += items.length;

      // Conta ids ainda não vistos — usado para parar quando o back ignora ?page.
      let novos = 0;
      for (const it of items) {
        const id = extrairId(it);
        if (id == null) { novos++; continue; }
        const chave = String(id);
        if (!vistos.has(chave)) { vistos.add(chave); novos++; }
      }

      let salvosPagina = items.length;
      if (typeof onPagina === "function") {
        const salvos = await onPagina(items, { pagina, lastPage });
        if (Number.isFinite(salvos)) { salvosPagina = salvos; totalSalvo += salvos; }
        else { totalSalvo += items.length; }
      }

      // Log diagnóstico por página (back metadata + recebido + novos + salvos)
      console.log(
        `[SYNC_PAGINADO] ${label} pagina ${pagina}: ` +
        `recebidos=${items.length}, novos=${novos}, salvos=${salvosPagina} | ` +
        `back: total=${metaTotal ?? "?"}, per_page=${metaPerPage ?? "?"}, ` +
        `current_page=${metaCurrentPage ?? "?"}, last_page=${metaLastPage ?? "?"}`,
      );

      if (typeof onProgress === "function") {
        const pct = lastPage ? Math.round((pagina / lastPage) * 100) : Math.min(95, paginas * 5);
        onProgress(pct, `${label}: ${totalRecebido} recebidos...`);
      }

      let continuar;
      if (lastPage != null) {
        continuar = pagina < lastPage;
      } else {
        // Sem metadados de paginação: segue enquanto a página vier "cheia" e
        // trouxer registros novos (se só repetir ids, o back ignora ?page → para).
        continuar = items.length >= perPage && novos > 0;
      }

      pagina++;
      if (!continuar || pagina > MAX_PAGINAS) break;
    }

    if (lastPage == null && totalBack == null) {
      console.warn(
        `[SYNC_PAGINADO] ${label}: back NÃO retornou metadados de paginação ` +
        `(total/last_page). Parada baseada em heurística de ids novos — ` +
        `se o back ignora ?page, registros além da 1ª página podem não vir. ` +
        `Ajuste o endpoint para retornar um paginator (total, per_page, last_page, data).`,
      );
    }

    console.log(
      `[SYNC_PAGINADO] ${label} CONCLUÍDO: ` +
      `esperado(back)=${totalBack ?? "desconhecido"}, recebido=${totalRecebido}, ` +
      `salvo=${totalSalvo}, paginas=${paginas}`,
    );
    return { totalRecebido, totalSalvo, paginas, totalBack, lastPage };
  },

  /**
   * Sincronização completa: produtos, clientes e formas de pagamento.
   *
   * @param {object} [options]
   * @param {function} [options.onProgress] - callback(percent, message)
   */
  async sincronizarTudo({ onProgress } = {}) {
    if (!isDesktopMode()) return;
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    await TerminalConfigService.garantirConfiguracaoNfceLocal();
    const { apiUrl, token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado. Faça login novamente.");
    return SincronizacaoService.sincronizarInicial({ apiUrl, token, onProgress });
  },

  /**
   * Sincroniza somente os produtos.
   */
  async sincronizarProdutos({ onProgress } = {}) {
    if (!isDesktopMode()) return;
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    const { apiUrl, token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado.");

    const api     = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:        "application/json",
    };

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    const idEmpresaRaw = await api.config.get("id_empresa");
    const id_empresa   = idEmpresaRaw ? parseInt(idEmpresaRaw, 10) : null;

    progress(5, "Baixando produtos...");

    // Coleta produtos que possuem combinações para sincronizá-las em seguida
    const produtosComCombinacao = [];

    const { totalRecebido, totalSalvo, paginas, totalBack } = await this._baixarPaginado({
      baseUrl, headers,
      path:    "/api/offline/produtos",
      perPage: 200,
      label:   "Produtos",
      idOf:    (p) => p?.id ?? p?.id_produto ?? p?.id_remoto ?? null,
      onProgress: (p, m) => progress(5 + Math.round(p * 0.80), m),
      onPagina: async (items) => {
        if (!items.length) return 0;

        const normalized = items.map((p) => ({
          ...p,
          id_empresa: p.id_empresa ?? id_empresa,
          id_remoto: String(p.id ?? p.id_remoto ?? ""),
        }));

        // Upsert resiliente: o repo captura erros por linha e devolve a contagem.
        const res = await api.produtos.upsertMany(normalized);
        const salvosPagina = res && Number.isFinite(res.salvos) ? res.salvos : normalized.length;
        if (res && res.erros > 0) {
          console.error(
            `[SYNC_PRODUTOS] ${res.erros}/${normalized.length} produto(s) falharam no upsert nesta página.`,
            res.detalhesErros || "",
          );
        }

        // Salva lotes dos produtos que possuem a propriedade lotes (não pode
        // abortar o sync de produtos se um lote falhar).
        const todosLotes = normalized.flatMap((p) =>
          Array.isArray(p.lotes) ? p.lotes : []
        );
        if (todosLotes.length > 0) {
          try {
            await api.produtoLotes.salvarLotes(todosLotes);
          } catch (e) {
            console.error(`[SYNC_PRODUTOS] Falha ao salvar lotes desta página:`, e.message);
          }
        }

        // Detecta produtos com combinações e salva as que vieram no payload
        for (const p of normalized) {
          const idProduto = p.id ?? p.id_produto ?? null;

          // Normaliza o array de combinações (backend pode usar nomes diferentes)
          const combinacoesRaw =
            (Array.isArray(p.combinacoes)          ? p.combinacoes          : null) ??
            (Array.isArray(p.produto_combinacoes)  ? p.produto_combinacoes  : null) ??
            (Array.isArray(p.variacoes)            ? p.variacoes            : null) ??
            [];

          const possuiCombinacao =
            combinacoesRaw.length > 0 ||
            p.possui_combinacao === true ||
            p.possui_combinacao === 1 ||
            p.possui_combinacao === "1" ||
            (Number(p.total_combinacoes) || 0) > 0 ||
            p.variavel === true ||
            p.variavel === 1 ||
            p.variavel === "1";

          // Salva combinações que vieram embutidas no payload do produto
          if (combinacoesRaw.length > 0 && idProduto) {
            const combinacoesNormalizadas = combinacoesRaw.map((c) => ({
              ...c,
              id_produto: c.id_produto ?? idProduto,
              id_empresa: c.id_empresa ?? p.id_empresa ?? id_empresa,
            }));
            try {
              await api.produtoCombinacoes.salvar(combinacoesNormalizadas);
            } catch (e) {
              console.warn(`[SYNC_COMBINACOES_FROM_PRODUTO] Falha ao salvar combinações do produto ${idProduto}:`, e.message);
            }
          }

          // Se variável mas sem combinações no payload, agenda busca via API separada
          if (possuiCombinacao && combinacoesRaw.length === 0 && idProduto) {
            produtosComCombinacao.push(idProduto);
          }
        }

        return salvosPagina;
      },
    });

    // Atualiza a última sincronização SOMENTE após concluir todas as páginas.
    await api.sync.atualizarControle("produtos", new Date().toISOString());

    // Reconciliação: compara o total esperado (back) x recebido x salvo no banco local.
    let totalNoBanco = null;
    let totalAtivosNoBanco = null;
    try {
      totalNoBanco       = await api.produtos.contarTodos();
      totalAtivosNoBanco = await api.produtos.contarProdutos();
    } catch (e) {
      console.warn("[SYNC_PRODUTOS] Não foi possível contar produtos no banco local:", e.message);
    }
    console.log(
      `[SYNC_PRODUTOS] RECONCILIAÇÃO: esperado(back)=${totalBack ?? "desconhecido"}, ` +
      `recebido=${totalRecebido}, salvo(lote)=${totalSalvo}, ` +
      `no_banco_total=${totalNoBanco ?? "?"}, no_banco_ativos=${totalAtivosNoBanco ?? "?"}, ` +
      `paginas=${paginas}`,
    );
    if (totalBack != null && totalNoBanco != null && totalNoBanco < totalBack) {
      console.warn(
        `[SYNC_PRODUTOS] DIVERGÊNCIA: o back informa ${totalBack} produto(s), ` +
        `mas apenas ${totalNoBanco} foram gravados localmente. ` +
        `Verifique os logs [SYNC_PAGINADO]/[REPO_PRODUTOS] acima.`,
      );
    }
    if (totalNoBanco != null && totalAtivosNoBanco != null && totalAtivosNoBanco < totalNoBanco) {
      console.warn(
        `[SYNC_PRODUTOS] ATENÇÃO: ${totalNoBanco - totalAtivosNoBanco} produto(s) gravados ` +
        `estão inativos (ativo=0) e NÃO aparecem na busca do PDV OFF.`,
      );
    }
    progress(90, `Produtos sincronizados: ${totalSalvo}`);

    // Sincroniza combinações para cada produto que possui variações
    if (produtosComCombinacao.length > 0) {
      progress(92, `Sincronizando combinações de ${produtosComCombinacao.length} produto(s)...`);
      for (const id_produto of produtosComCombinacao) {
        try {
          await this.sincronizarProdutosCombinacoes({ id_produto, full: true });
        } catch (e) {
          console.warn(`[SYNC_PRODUTOS] Falha ao sincronizar combinações do produto ${id_produto}:`, e.message);
        }
      }
    }

    progress(100, `Produtos sincronizados: ${totalSalvo}`);
    return totalSalvo;
  },

  /**
   * Sincroniza somente os clientes.
   */
  async sincronizarClientes({ onProgress } = {}) {
    if (!isDesktopMode()) return;
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    const { apiUrl, token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado.");

    const api     = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:        "application/json",
    };

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    const idEmpresaRaw = await api.config.get("id_empresa");
    const id_empresa   = idEmpresaRaw ? parseInt(idEmpresaRaw, 10) : null;

    progress(5, "Baixando clientes...");

    const { totalRecebido, totalSalvo, paginas } = await this._baixarPaginado({
      baseUrl, headers,
      path:    "/api/offline/clientes",
      perPage: 200,
      label:   "Clientes",
      idOf:    (c) => c?.id ?? c?.id_cliente ?? c?.id_remoto ?? null,
      onProgress: (p, m) => progress(5 + Math.round(p * 0.90), m),
      onPagina: async (items) => {
        if (!items.length) return 0;
        const normalized = items.map((c) => ({
          ...c,
          id_empresa: c.id_empresa ?? id_empresa,
          id_remoto: String(c.id ?? c.id_remoto ?? ""),
        }));
        await api.clientes.upsertMany(normalized);
        return normalized.length;
      },
    });

    // Atualiza a última sincronização SOMENTE após concluir todas as páginas.
    await api.sync.atualizarControle("clientes", new Date().toISOString());
    console.log(`[SYNC_CLIENTES] total recebido=${totalRecebido}, total salvo=${totalSalvo}, paginas=${paginas}`);
    progress(100, `Clientes sincronizados: ${totalSalvo}`);
    return totalSalvo;
  },

  /**
   * Sincroniza somente as formas de pagamento.
   */
  async sincronizarFormasPagamento({ onProgress } = {}) {
    if (!isDesktopMode()) return;
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    const { apiUrl, token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado.");

    const api     = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:        "application/json",
    };

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    progress(10, "Baixando formas de pagamento...");
    const resp = await fetch(`${baseUrl}/api/offline/formas-pagamento`, { headers });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

    const json  = await resp.json();
    const items = json.data ?? json ?? [];

    if (items.length > 0) {
      await api.formasPagamento.salvar(items);
    }

    await api.sync.atualizarControle("formas_pagamento", new Date().toISOString());
    console.log(`[SYNC_FORMAS_PAGAMENTO] total recebido=${items.length}, total salvo=${items.length}, paginas=1`);
    progress(100, `Formas de pagamento sincronizadas: ${items.length}`);
    return items.length;
  },

  /**
   * Sincroniza lotes de produto da API para o banco local.
   * Usa updated_after baseado no controle de sync para buscar somente atualizações.
   */
  async sincronizarProdutosLotes({ onProgress } = {}) {
    if (!isDesktopMode()) return 0;
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    const { apiUrl, token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado.");

    const api     = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:        "application/json",
    };

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    progress(5, "Verificando controle de sync de lotes...");

    // Busca último sync de lotes
    const controles = await api.sync.getControle();
    const controle  = (controles || []).find((c) => c.entidade === "produtos_lotes");
    const updatedAfterParam = controle && controle.ultimo_sync
      ? `&updated_after=${encodeURIComponent(controle.ultimo_sync)}`
      : "";

    progress(10, "Baixando lotes de produto...");

    const { totalRecebido, totalSalvo, paginas } = await this._baixarPaginado({
      baseUrl, headers,
      path:    "/api/offline/produtos-lotes",
      perPage: 200,
      query:   updatedAfterParam,
      label:   "Lotes",
      idOf:    (l) => l?.id_produto_lote ?? l?.id ?? l?.id_remoto ?? null,
      onProgress: (p, m) => progress(10 + Math.round(p * 0.85), m),
      onPagina: async (items) => {
        if (!items.length) return 0;
        await api.produtoLotes.salvarLotes(items);
        return items.length;
      },
    });

    // Atualiza a última sincronização SOMENTE após concluir todas as páginas.
    await api.sync.atualizarControle("produtos_lotes", new Date().toISOString());
    console.log(`[SYNC_LOTES] total recebido=${totalRecebido}, total salvo=${totalSalvo}, paginas=${paginas}`);
    progress(100, `Lotes sincronizados: ${totalSalvo}`);
    return totalSalvo;
  },

  /**
   * Sincroniza combinações de produto da API para o banco local.
   *
   * Modos de operação:
   *  - incremental (padrão): usa updated_after do controle de sync.
   *  - por produto (id_produto + full: true): baixa todas as combinações ativas
   *    do produto, sem depender de updated_after, e remove localmente as que
   *    não vieram no retorno (mantém consistência).
   *
   * @param {object} opts
   * @param {number}   [opts.id_produto]  - filtra por produto específico
   * @param {boolean}  [opts.full]        - força recarga completa (sem updated_after)
   * @param {Function} [opts.onProgress]
   */
  async sincronizarProdutosCombinacoes({ id_produto, full = false, onProgress } = {}) {
    if (!isDesktopMode()) return 0;
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    const { apiUrl, token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado.");

    const api     = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:        "application/json",
    };

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    // Monta query string
    let queryExtra = "";

    if (id_produto) {
      // Modo por produto: ignora updated_after, retorna tudo do produto
      queryExtra += `&id_produto=${encodeURIComponent(id_produto)}`;
    } else if (!full) {
      // Modo incremental: usa controle de sync
      progress(5, "Verificando controle de sync de combinações...");
      const controles = await api.sync.getControle();
      const controle  = (controles || []).find((c) => c.entidade === "produtos_combinacoes");
      if (controle && controle.ultimo_sync) {
        queryExtra += `&updated_after=${encodeURIComponent(controle.ultimo_sync)}`;
      }
    }

    const filtrosLog = id_produto ? `id_produto=${id_produto}` : (full ? "full" : "incremental");
    console.log("[SYNC_COMBINACOES] filtros:", filtrosLog);

    progress(10, "Baixando combinações de produto...");
    const todasRecebidas = [];

    const { totalRecebido, totalSalvo, paginas } = await this._baixarPaginado({
      baseUrl, headers,
      path:    "/api/offline/produtos-combinacoes",
      perPage: 200,
      query:   queryExtra,
      label:   "Combinações",
      idOf:    (c) => c?.id_produto_combinacao ?? c?.id ?? null,
      onProgress: (p, m) => progress(10 + Math.round(p * 0.75), m),
      onPagina: async (items) => {
        if (!items.length) return 0;

        // Filtra deletadas do servidor e processa
        const ativas    = items.filter((c) => !c.deleted_at);
        const deletadas = items.filter((c) =>  c.deleted_at);

        let salvos = 0;
        if (ativas.length > 0) {
          await api.produtoCombinacoes.salvar(ativas);
          todasRecebidas.push(...ativas);
          salvos = ativas.length;
        }

        // Remove localmente as que o servidor marcou como deletadas
        if (deletadas.length > 0) {
          const ids = deletadas
            .map((c) => c.id_produto_combinacao ?? c.id)
            .filter(Boolean);
          if (ids.length > 0) {
            await api.produtoCombinacoes.removerPorIds(ids);
          }
        }

        return salvos;
      },
    });

    // No modo full por produto: remove localmente combinações que não vieram mais
    if (id_produto && full && todasRecebidas.length >= 0) {
      const idsRecebidos = todasRecebidas
        .map((c) => c.id_produto_combinacao ?? c.id)
        .filter(Boolean);
      await api.produtoCombinacoes.removerAusentesDosProduto(id_produto, idsRecebidos);
    }

    // Atualiza controle de sync apenas no modo geral (não por produto) e somente
    // após concluir todas as páginas.
    if (!id_produto) {
      await api.sync.atualizarControle("produtos_combinacoes", new Date().toISOString());
    }

    console.log(`[SYNC_COMBINACOES] total recebido=${totalRecebido}, total salvo=${totalSalvo}, paginas=${paginas}`);
    progress(100, `Combinações sincronizadas: ${totalSalvo}`);
    return totalSalvo;
  },

  /**
   * Envia vendas pendentes para a API.
   */
  async sincronizarVendasPendentes({ onProgress } = {}) {
    if (!isDesktopMode()) return { enviadas: 0, erros: 0 };
    const { apiUrl, token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado.");

    // Valida vínculo do terminal antes de enviar dados ao back
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    await TerminalConfigService.garantirConfiguracaoNfceLocal();

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    progress(10, "Enviando vendas pendentes...");
    const resultado = await SincronizacaoService.sincronizarVendasPendentes(apiUrl, token);
    progress(100, `Enviadas: ${resultado.enviadas}, Erros: ${resultado.erros}`);
    return resultado;
  },

  /**
   * Sincronização completa manual (disparada pelo botão "Sincronizar").
   * Ordem: produtos → combinações incrementais → lotes → formas de pagamento → clientes
   *
   * A sincronização de produtos já cuida de disparar sincronizarProdutosCombinacoes
   * em modo full para cada produto que possui variações. O passo de combinações
   * aqui é o incremental (demais atualizações não relacionadas a produtos novos).
   */
  async sincronizarCompleto({ onProgress } = {}) {
    // Valida vínculo do terminal antes da sincronização completa
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    await TerminalConfigService.garantirConfiguracaoNfceLocal();

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    progress(0, "Iniciando sincronização completa...");
    await this.sincronizarProdutos({ onProgress: (p, m) => progress(Math.round(p * 0.30), m) });

    progress(30, "Sincronizando combinações...");
    await this.sincronizarProdutosCombinacoes({ onProgress: (p, m) => progress(30 + Math.round(p * 0.12), m) });

    progress(42, "Sincronizando lotes...");
    await this.sincronizarProdutosLotes({ onProgress: (p, m) => progress(42 + Math.round(p * 0.12), m) });

    progress(54, "Sincronizando formas de pagamento...");
    await this.sincronizarFormasPagamento({ onProgress: (p, m) => progress(54 + Math.round(p * 0.07), m) });

    progress(61, "Sincronizando listas de preço...");
    await this.sincronizarListaPrecos({ onProgress: (p, m) => progress(61 + Math.round(p * 0.07), m) });

    progress(68, "Sincronizando clientes...");
    await this.sincronizarClientes({ onProgress: (p, m) => progress(68 + Math.round(p * 0.14), m) });

    progress(82, "Sincronizando vendedores...");
    await this.sincronizarVendedores({ onProgress: (p, m) => progress(82 + Math.round(p * 0.09), m) });

    progress(91, "Sincronizando grupos de produto...");
    await this.sincronizarGrupos({ onProgress: (p, m) => progress(91 + Math.round(p * 0.09), m) });

    // Reconcilia status de NFC-e canceladas no back/web (não bloqueia o fluxo)
    try {
      await NfceStatusSyncService.sincronizarStatusNfces();
    } catch (e) {
      console.warn("[SyncPdvDesktopService] reconciliação de status NFC-e:", e?.message ?? e);
    }

    progress(100, "Sincronização completa concluída.");
  },

  /**
   * Sincroniza vendedores da API para o banco local.
   */
  async sincronizarVendedores({ onProgress } = {}) {
    if (!isDesktopMode()) return 0;
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    const { apiUrl, token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado.");

    const api     = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:        "application/json",
    };

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    progress(10, "Baixando vendedores...");

    const { totalRecebido, totalSalvo, paginas } = await this._baixarPaginado({
      baseUrl, headers,
      path:    "/api/offline/vendedores",
      perPage: 200,
      label:   "Vendedores",
      idOf:    (v) => v?.id ?? v?.id_vendedor ?? v?.id_remoto ?? null,
      onProgress: (p, m) => progress(10 + Math.round(p * 0.85), m),
      onPagina: async (items) => {
        if (!items.length) return 0;
        await api.vendedores.salvar(items);
        return items.length;
      },
    });

    // Atualiza a última sincronização SOMENTE após concluir todas as páginas.
    await api.sync.atualizarControle("vendedores", new Date().toISOString());
    console.log(`[SYNC_VENDEDORES] total recebido=${totalRecebido}, total salvo=${totalSalvo}, paginas=${paginas}`);
    progress(100, `Vendedores sincronizados: ${totalSalvo}`);
    return totalSalvo;
  },

  /**
   * Sincroniza grupos de produto da API para o banco local.
   */
  async sincronizarGrupos({ onProgress } = {}) {
    if (!isDesktopMode()) return 0;
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    const { apiUrl, token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado.");

    const api     = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:        "application/json",
    };

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    progress(10, "Baixando grupos de produto...");

    const { totalRecebido, totalSalvo, paginas } = await this._baixarPaginado({
      baseUrl, headers,
      path:    "/api/offline/produtos-grupos",
      perPage: 200,
      label:   "Grupos",
      idOf:    (g) => g?.id_grupo ?? g?.id ?? g?.id_remoto ?? null,
      onProgress: (p, m) => progress(10 + Math.round(p * 0.85), m),
      onPagina: async (items) => {
        if (!items.length) return 0;
        await api.grupos.salvar(items);
        return items.length;
      },
    });

    // Atualiza a última sincronização SOMENTE após concluir todas as páginas.
    await api.sync.atualizarControle("grupos", new Date().toISOString());
    console.log(`[SYNC_GRUPOS] total recebido=${totalRecebido}, total salvo=${totalSalvo}, paginas=${paginas}`);
    progress(100, `Grupos sincronizados: ${totalSalvo}`);
    return totalSalvo;
  },

  /**
   * Sincroniza listas de preço da API para o banco local.
   * Suporta paginação (?page, ?per_page) e sync incremental (?updated_after).
   *
   * Endpoint: GET /api/offline/lista-precos
   * IPC:      api.listaPreco.salvar(listas)
   */
  async sincronizarListaPrecos({ onProgress } = {}) {
    if (!isDesktopMode()) return 0;
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    const { apiUrl, token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado.");

    const api     = getElectronAPI();
    const baseUrl = apiUrl.replace(/\/$/, "");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept:        "application/json",
    };

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    progress(5, "Verificando controle de sync de listas de preço...");

    const controles = await api.sync.getControle();
    const controle  = (controles || []).find((c) => c.entidade === "lista_preco");
    const updatedAfterParam = controle && controle.ultimo_sync
      ? `&updated_after=${encodeURIComponent(controle.ultimo_sync)}`
      : "";

    progress(10, "Baixando listas de preço...");

    const { totalRecebido, totalSalvo, paginas } = await this._baixarPaginado({
      baseUrl, headers,
      path:    "/api/offline/lista-precos",
      perPage: 200,
      query:   updatedAfterParam,
      label:   "Listas de preço",
      idOf:    (l) => l?.id_lista_preco ?? l?.id ?? l?.id_remoto ?? null,
      onProgress: (p, m) => progress(10 + Math.round(p * 0.85), m),
      onPagina: async (items) => {
        if (!items.length) return 0;
        await api.listaPreco.salvar(items);
        return items.length;
      },
    });

    // Atualiza a última sincronização SOMENTE após concluir todas as páginas.
    await api.sync.atualizarControle("lista_preco", new Date().toISOString());
    console.log(`[SYNC_LISTA_PRECOS] total recebido=${totalRecebido}, total salvo=${totalSalvo}, paginas=${paginas}`);
    progress(100, `Listas de preço sincronizadas: ${totalSalvo}`);
    return totalSalvo;
  },

  /**
   * Recarrega TODAS as combinações do servidor do zero (sem updated_after).
   * Útil para suporte/debug quando as combinações locais estão inconsistentes.
   */
  async recarregarCombinacoes({ onProgress } = {}) {
    if (!isDesktopMode()) return 0;
    console.log("[SYNC_COMBINACOES] iniciando recarga completa de combinações...");
    return this.sincronizarProdutosCombinacoes({ full: true, onProgress });
  },

  /**
   * Sincronização inicial completa — executada na primeira configuração do terminal.
   * @param {{ onProgress?: (pct: number, msg: string) => void }} options
   */
  async sincronizacaoInicialCompleta({ onProgress } = {}) {
    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    const avisos = [];

    progress(0, "Configurando terminal...");
    progress(1, "Validando terminal e configuração NFC-e...");
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    await TerminalConfigService.garantirConfiguracaoNfceLocal();

    // Etapa crítica: sem contexto fiscal completo não libera o PDV.
    progress(6, "Baixando configuração fiscal...");
    try {
      await FiscalContextPdvDesktopService.sincronizarContextoFiscal();
      await TerminalConfigService.garantirConfiguracaoTerminalParaEmissao();
    } catch (e) {
      if (TerminalConfigService.isCodigoConfiguracaoNfceAusente(e?.code)) {
        throw e;
      }
      const err = new Error(
        "Não foi possível baixar a configuração fiscal do terminal. Verifique a internet e tente novamente."
      );
      err.code = "FISCAL_SYNC_INICIAL_FALHOU";
      throw err;
    }

    const executarEtapaNaoCritica = async (inicio, fim, mensagem, fn) => {
      progress(inicio, mensagem);
      try {
        await fn((p, m) => progress(inicio + Math.round((p * (fim - inicio)) / 100), m || mensagem));
      } catch (e) {
        console.warn(`[SYNC_INICIAL] ${mensagem}:`, e?.message || e);
        avisos.push(mensagem);
      }
    };

    // Etapas não críticas: falhas geram aviso, mas não bloqueiam se fiscal estiver OK.
    await executarEtapaNaoCritica(12, 20, "Sincronizando formas de pagamento...", (onProgress) =>
      this.sincronizarFormasPagamento({ onProgress })
    );

    await executarEtapaNaoCritica(20, 28, "Sincronizando vendedores...", (onProgress) =>
      this.sincronizarVendedores({ onProgress })
    );

    await executarEtapaNaoCritica(28, 36, "Sincronizando grupos de produtos...", (onProgress) =>
      this.sincronizarGrupos({ onProgress })
    );

    await executarEtapaNaoCritica(36, 60, "Sincronizando produtos...", (onProgress) =>
      this.sincronizarProdutos({ onProgress })
    );

    await executarEtapaNaoCritica(60, 68, "Sincronizando lotes...", (onProgress) =>
      this.sincronizarProdutosLotes({ onProgress })
    );

    await executarEtapaNaoCritica(68, 74, "Sincronizando combinações...", (onProgress) =>
      this.sincronizarProdutosCombinacoes({ full: true, onProgress })
    );

    await executarEtapaNaoCritica(74, 84, "Sincronizando clientes...", (onProgress) =>
      this.sincronizarClientes({ onProgress })
    );

    await executarEtapaNaoCritica(84, 90, "Sincronizando listas de preço...", (onProgress) =>
      this.sincronizarListaPrecos({ onProgress })
    );

    await executarEtapaNaoCritica(90, 94, "Finalizando configuração...", () =>
      CaixaSyncService.sincronizarCaixasPendentes()
    );

    await executarEtapaNaoCritica(94, 97, "Finalizando configuração...", async () => {
      await TerminalConfigService.garantirConfiguracaoNfceLocal();
      await this.sincronizarVendasPendentes();
    });

    await executarEtapaNaoCritica(97, 99, "Finalizando configuração...", () =>
      this.sincronizarNfcesPendentes()
    );

    progress(100, "Configuração concluída.");
    return {
      fiscalOk: true,
      avisos,
      parcial: avisos.length > 0,
    };
  },

  /**
   * Transmite uma única NFC-e assinada ao backend para envio à SEFAZ.
   *
   * Busca a NFC-e pelo uuid, valida o xml_assinado e envia para:
   * POST /api/offline/nfce/transmitir-xml
   *
   * Atualiza o status local com base no retorno:
   *   - autorizada / ja_autorizada → status_fiscal='autorizada', status_sync='sincronizada'
   *   - rejeitada                  → status_fiscal='rejeitada', codigo_rejeicao, motivo_erro
   *   - qualquer outro             → status_fiscal='pendente_transmissao_backend' (mantém na fila)
   *
   * @param {string} uuid_venda_terminal
   * @returns {Promise<{ status: string, protocolo?: string, motivo?: string, codigo?: string }>}
   */
  async transmitirNfce(uuid_venda_terminal) {
    if (!isDesktopMode()) throw new Error("Modo desktop não disponível.");

    // Operação fiscal crítica: valida vínculo antes de transmitir
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    await TerminalConfigService.garantirConfiguracaoNfceLocal();

    const api = getElectronAPI();
    if (!api) throw new Error("Electron API não disponível.");

    const { token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado. Faça login novamente.");

    const nfce = await api.nfce.getByUuid(uuid_venda_terminal);
    if (!nfce) throw new Error(`NFC-e não encontrada: ${uuid_venda_terminal}`);

    // Bloqueia retransmissão de nota já autorizada
    if (nfce.status_fiscal === "autorizada") {
      return { status: "ja_autorizada", protocolo: nfce.protocolo ?? null };
    }

    if (!nfce.xml_assinado) throw new Error("NFC-e sem XML assinado. Gere e assine o XML antes de transmitir.");

    const terminalConfig = await _obterTerminalConfigFiscalParaTransmissao(api);

    const payload = {
      uuid_terminal: terminalConfig.uuid_terminal,
      device_id: terminalConfig.device_id,
      id_terminal_venda: terminalConfig.id_terminal_venda,
      id_configuracao: terminalConfig.id_configuracao,
      id_configuracao_nfce: terminalConfig.id_configuracao_nfce,
      nfces: [_montarEntradaTransmissaoComContexto(nfce, terminalConfig)],
    };

    console.log("[TRANSMITIR_XML_PAYLOAD]", {
      uuid_terminal: payload.uuid_terminal,
      has_device_id: !!payload.device_id,
      id_terminal_venda: payload.id_terminal_venda,
      id_configuracao: payload.id_configuracao,
      total_nfces: payload.nfces?.length || 0,
      primeira_nfce: {
        uuid_venda_terminal: payload.nfces?.[0]?.uuid_venda_terminal,
        chave: payload.nfces?.[0]?.chave,
        serie: payload.nfces?.[0]?.serie,
        numero: payload.nfces?.[0]?.numero,
        modelo: payload.nfces?.[0]?.modelo,
        has_xml_assinado: !!payload.nfces?.[0]?.xml_assinado,
      },
    });

    const resposta = await ApiOfflineService.transmitirXmlNfce(payload);

    const resultados = resposta.resultados ?? resposta.data ?? [];
    const resultado  = resultados.find((r) => r.uuid_venda_terminal === uuid_venda_terminal)
      ?? resultados[0]
      ?? null;

    if (!resultado) {
      throw new Error("Backend não retornou resultado para esta NFC-e.");
    }

    const { status, protocolo, xml_autorizado, motivo, codigo } = resultado;

    if (status === "autorizada" || status === "ja_autorizada") {
      await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "autorizada", {
        status_sync:    "sincronizada",
        protocolo:      protocolo      ?? null,
        xml_autorizado: xml_autorizado ?? null,
      });
      try {
        await api.nfce.gerarDanfeHtml(uuid_venda_terminal);
      } catch (e) {
        console.warn("[SyncPdvDesktopService] Falha ao gerar DANFE local após autorização:", e?.message ?? e);
      }
    } else if (status === "rejeitada") {
      await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "rejeitada", {
        status_sync:     "erro",
        codigo_rejeicao: codigo ?? null,
        motivo_erro:     motivo ?? "Rejeitada pela SEFAZ",
        ultimo_erro:     motivo ?? "Rejeitada pela SEFAZ",
      });
    } else {
      // Erro desconhecido — mantém na fila para retry automático
      await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "pendente_transmissao_backend", {
        status_sync: "pendente",
        ultimo_erro: motivo ?? `Status inesperado: ${status}`,
      });
    }

    return resultado;
  },

  /**
   * Transmite NFC-e assinadas/pendentes ao backend para envio à SEFAZ.
   *
   * Busca todas as NFC-e com status_fiscal em:
   *   'xml_assinado', 'pendente_transmissao_backend', 'erro_transmissao'
   *
   * NÃO processa: autorizada, rejeitada, cancelada.
   *
   * @param {object} [options]
   * @param {function} [options.onProgress] - callback(percent, message)
   * @returns {Promise<{ transmitidas: number, erros: number }>}
   */
  async sincronizarNfcesPendentes({ onProgress } = {}) {
    if (!isDesktopMode()) return { transmitidas: 0, erros: 0 };

    // Valida vínculo do terminal — operação fiscal crítica
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
    await TerminalConfigService.garantirConfiguracaoNfceLocal();

    const api = getElectronAPI();
    if (!api) return { transmitidas: 0, erros: 0 };

    const { token } = await _getCredentials();
    if (!token) throw new Error("Token não encontrado.");

    // Antes de transmitir, reconcilia status de NFC-e canceladas no back/web.
    try {
      await NfceStatusSyncService.sincronizarStatusNfces();
    } catch (e) {
      console.warn("[SyncPdvDesktopService] reconciliação de status NFC-e:", e?.message ?? e);
    }

    const progress = (pct, msg) => {
      if (typeof onProgress === "function") onProgress(pct, msg);
    };

    progress(10, "Buscando NFC-e pendentes de transmissão...");
    const pendentes = (await api.nfce.listarPendentesTransmissaoBackend()) || [];

    if (pendentes.length === 0) {
      progress(100, "Nenhuma NFC-e pendente de transmissão.");
      return { transmitidas: 0, erros: 0 };
    }

    const terminalConfig = await _obterTerminalConfigFiscalParaTransmissao(api);

    // listarPendentesTransmissaoBackend só retorna o cabeçalho — precisamos
    // recarregar cada NFC-e via getByUuid para obter itens e pagamentos e
    // assim montar o payload completo da venda exigido pelo back_nuts.
    const nfces = [];
    for (const p of pendentes) {
      const completa = await api.nfce.getByUuid(p.uuid_venda_terminal);
      if (!completa || !completa.xml_assinado) continue;
      nfces.push(_montarEntradaTransmissaoComContexto(completa, terminalConfig));
    }

    if (nfces.length === 0) {
      progress(100, "Nenhuma NFC-e válida para transmitir.");
      return { transmitidas: 0, erros: 0 };
    }

    progress(30, `Transmitindo ${nfces.length} NFC-e(s) ao backend...`);

    const payload = {
      uuid_terminal: terminalConfig.uuid_terminal,
      device_id: terminalConfig.device_id,
      id_terminal_venda: terminalConfig.id_terminal_venda,
      id_configuracao: terminalConfig.id_configuracao,
      id_configuracao_nfce: terminalConfig.id_configuracao_nfce,
      nfces,
    };

    console.log("[TRANSMITIR_XML_PAYLOAD]", {
      uuid_terminal: payload.uuid_terminal,
      has_device_id: !!payload.device_id,
      id_terminal_venda: payload.id_terminal_venda,
      id_configuracao: payload.id_configuracao,
      total_nfces: payload.nfces?.length || 0,
      primeira_nfce: {
        uuid_venda_terminal: payload.nfces?.[0]?.uuid_venda_terminal,
        chave: payload.nfces?.[0]?.chave,
        serie: payload.nfces?.[0]?.serie,
        numero: payload.nfces?.[0]?.numero,
        modelo: payload.nfces?.[0]?.modelo,
        has_xml_assinado: !!payload.nfces?.[0]?.xml_assinado,
      },
    });

    const resposta = await ApiOfflineService.transmitirXmlNfce(payload);
    const resultados = resposta.resultados ?? resposta.data ?? [];

    let transmitidas = 0;
    let erros        = 0;

    for (const resultado of resultados) {
      const { uuid_venda_terminal, status, protocolo, xml_autorizado, motivo } = resultado;
      try {
        // Proteção contra duplicidade: verifica status atual antes de atualizar
        const atual = await api.nfce.getByUuid(uuid_venda_terminal);
        if (atual && atual.status_fiscal === "autorizada") {
          transmitidas++;
          continue;
        }

        if (status === "autorizada" || status === "ja_autorizada") {
          await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "autorizada", {
            status_sync:    "sincronizada",
            protocolo:      protocolo       ?? null,
            xml_autorizado: xml_autorizado  ?? null,
          });
          try {
            await api.nfce.gerarDanfeHtml(uuid_venda_terminal);
          } catch (e) {
            console.warn("[SyncPdvDesktopService] Falha ao gerar DANFE local após autorização:", e?.message ?? e);
          }
          transmitidas++;
        } else if (status === "rejeitada") {
          await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "rejeitada", {
            status_sync:     "erro",
            codigo_rejeicao: resultado.codigo ?? null,
            motivo_erro:     motivo ?? "Rejeitada pela SEFAZ",
            ultimo_erro:     motivo ?? "Rejeitada pela SEFAZ",
          });
          erros++;
        } else {
          await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "erro_transmissao", {
            ultimo_erro: motivo ?? "Erro desconhecido na transmissão",
          });
          erros++;
        }
      } catch (e) {
        console.error("[SyncPdvDesktopService] Erro ao atualizar NFC-e:", uuid_venda_terminal, e.message);
        erros++;
      }
    }

    progress(100, `Transmissão concluída: ${transmitidas} autorizada(s), ${erros} erro(s).`);
    return { transmitidas, erros };
  },

  /**
   * Alias de sincronizarNfcesPendentes — mantido para compatibilidade.
   * Transmite NFC-e assinadas/pendentes ao backend para envio à SEFAZ.
   */
  sincronizarNfcesAssinadas(options) {
    return this.sincronizarNfcesPendentes(options);
  },

  /**
   * Reconcilia o status local das NFC-e com o back/web (ex.: cancelamento feito
   * no painel web). Nunca lança; devolve um resumo.
   */
  sincronizarStatusNfces() {
    return NfceStatusSyncService.sincronizarStatusNfces();
  },
};

export default SyncPdvDesktopService;
